import { useMemo } from 'react';
import _ from 'lodash';
import { useRecoilValueLoadable } from 'recoil';
import { Link } from 'react-router-dom';
import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    AssignButton,
    Panel,
    PanelBody,
    PanelGrid,
    PanelGridAmount,
    PanelGridItem,
    PanelHeader,
    PanelLink,
} from 'components';
import { AdjustmentType, DisputeTypeText } from 'enums';
import { formatDate, getAdjustmentTypeLabel } from 'functions';
import { useAccountParam, useNavigation, useRefreshDispute, useShowAsEscalated } from 'hooks';
import { filteredDisputes, useAccountId, useDisputeReason, useRefreshAllDisputeSummaries, useUserInfo } from 'state';
import { DisputeSummary } from 'types';
import { DisputeDetail } from 'types';
import styles from './DisputeDetailHeader.module.scss';

type Props = {
    disputeDetails: DisputeDetail
};

const DisputeDetailHeader = ({ disputeDetails }: Props) => {
    const {
        adjustmentTypeId,
        assigneeEmail,
        assigneeFirstName,
        assigneeLastName,
        contactEmail,
        contactFirstName,
        contactLastName,
        disputeAmount,
        disputeNumber,
        disputeReasonId,
        disputeType,
        invoiceId,
        invoiceDetailLines = [],
        paymentDetailLines = [],
        paymentIdRef,
        submittedDateUtc,
        vendorId,
        vendorName,
    } = disputeDetails;
    const accountFromUrl = useAccountParam();
    const accountFromState = useAccountId();
    const accountId = accountFromUrl ?? accountFromState ?? '';
    const disputesLoadable = useRecoilValueLoadable(filteredDisputes(accountId));
    const { disputesUrl } = useNavigation();
    const user = useUserInfo();
    const refresh = useRefreshDispute(disputeNumber);
    const refreshDisputeSummaries = useRefreshAllDisputeSummaries();
    const vendor = _.startCase(_.toLower(vendorName));
    const disputeReason = useDisputeReason(disputeReasonId);
    const isInvoice = !_.isEmpty(invoiceDetailLines);
    const isPayment = !_.isEmpty(paymentDetailLines);
    const disputedItemHeader = isPayment ? "Payment" : "Invoice";
    const disputedItemId = isPayment ? paymentIdRef : invoiceId;
    const disputePoNumber = isPayment ? paymentDetailLines[0].voucherPoId : isInvoice ? invoiceDetailLines[0].poId : '';
    const disputedQuantity = isInvoice ? invoiceDetailLines[0].disputedQuantity ?? 0 : 0;
    const shouldShowAsEscalated = useShowAsEscalated(disputeDetails);
    const nextDispute = useMemo(() => {
        if (disputesLoadable.state !== 'hasValue') {
            return undefined;
        }

        const disputes = disputesLoadable.contents as DisputeSummary[];
        const currIndex = disputes.findIndex((dispute) => dispute.disputeNumber === disputeNumber);

        if (currIndex < 0) {
            return undefined;
        }

        return disputes[currIndex + 1]?.disputeNumber;
    }, [disputeNumber, disputesLoadable.contents, disputesLoadable.state]);

    return (
        <Panel className={styles.panel}>
            <PanelLink>
                <div className={styles.linkGroup}>
                    <Link to={disputesUrl}>Back to Disputes</Link>
                    {user?.isInternalUser && disputesLoadable.state === 'loading' && (
                        <span className={styles.nextLinkLoading}>Loading Next Dispute...</span>
                    )}
                    {user?.isInternalUser && nextDispute && (
                        <Link
                            to={`${disputesUrl}/${nextDispute}`}
                            className={styles.nextLink}
                            onClick={refreshDisputeSummaries}
                        >
                            Get Next Dispute
                        </Link>
                    )}
                </div>
            </PanelLink>

            <PanelBody className={styles.body}>
                <PanelGrid>
                    <div className={styles.bodyGridContainer}>
                        <div className={styles.grid}>
                            <PanelHeader className={styles.header}>
                                <div className={styles.heading}>
                                    Dispute {disputeNumber} | {vendor} - {vendorId} &nbsp;&nbsp;
                                    {shouldShowAsEscalated && <span className={styles.badge}><FontAwesomeIcon className={styles.badge} icon={faExclamationCircle} size="sm" /> Escalated </span>}
                                </div>
                            </PanelHeader>
                            <div className={styles.gridItem}>
                                <PanelGridItem
                                    label="Submitted:"
                                    value={`${submittedDateUtc && formatDate(submittedDateUtc)}`}
                                />
                            </div>
                            <div className={styles.gridItem}>
                                <PanelGridItem
                                    label="Claimed For:"
                                    value={disputedItemId ? `${disputedItemHeader}\n#${disputedItemId}` : `${disputedItemHeader}\n# Not Available`}
                                />
                            </div>
                            <div className={styles.gridItem}>
                                <PanelGridAmount
                                    label="Disputed Amount:"
                                    value={disputeAmount}
                                />
                            </div>
                            <div className={styles.gridItem}>
                                <PanelGridItem
                                    label="Disputing:"
                                    value={getAdjustmentTypeLabel(adjustmentTypeId)}
                                />
                            </div>
                            <div className={styles.gridItem}>
                                <PanelGridItem
                                    label="Dispute Type:"
                                    value={DisputeTypeText[disputeType] ?? disputeType}
                                />
                            </div>
                            <div className={styles.gridItem}>
                                <span className={styles.disputeReason}>
                                    <span className={styles.disputeReasonLabel}>
                                        Dispute Reason:
                                    </span>
                                    {disputeReason?.description ?? 'Not Found'}
                                </span>
                            </div>
                            <div className={styles.gridItem}>
                                {!_.isEmpty(disputePoNumber) &&
                                    <PanelGridItem
                                        label="PO Number:"
                                        value={`${disputePoNumber}`}
                                    />
                                }
                            </div>
                            {adjustmentTypeId === AdjustmentType.Quantity && disputedQuantity > 0 && (
                                <div className={styles.gridItem}>
                                    <PanelGridItem
                                        label="Disputed Quantity:"
                                        value={disputedQuantity}
                                    />
                                </div>
                            )}
                        </div>
                        <div className={styles.assignmentContainer}>
                            <div className={styles.assignmentItem}>
                                <span className={styles.label}>
                                    Contact
                                </span>
                                <span className={styles.value}>
                                    {`${contactFirstName} ${contactLastName}`}
                                </span>
                                <span className={styles.value}>
                                    {contactEmail}
                                </span>
                            </div>
                            <div className={styles.assignmentItem}>
                                {user?.isInternalUser ?
                                    assigneeEmail ? (
                                        <>
                                            <span className={styles.label}>
                                                Assignee
                                            </span>
                                            <span className={styles.value}>
                                                {`${assigneeFirstName} ${assigneeLastName}`}
                                            </span>
                                            <span className={styles.value}>
                                                {assigneeEmail}
                                            </span>
                                        </>
                                    ) : <>
                                        <span className={styles.label}>
                                            Assignee
                                        </span>
                                        <AssignButton disputeId={disputeNumber} onClick={refresh} />
                                    </>
                                    : <></>}
                            </div>
                        </div>
                    </div>
                </PanelGrid>
            </PanelBody>
        </Panel>
    );
};

export default DisputeDetailHeader;
